import { useEffect, useState, type FormEvent } from "react";

import {
  api,
  uploadsApi,
  sha256OfFile,
  putToPresignedUrl,
  type Account,
} from "../lib/api";

const MAX_FILE_SIZE = 100 * 1024 * 1024;

/**
 * Trade-log upload modal.
 *
 * Steps:
 *   1. Pick an account (from existing accounts dropdown).
 *   2. Pick a file (CSV / XLSX / PDF).
 *   3. We SHA-256 it in the browser (blocks briefly for big files).
 *   4. POST /uploads/init → get presigned URL.
 *   5. PUT the file directly to S3 via XHR with progress.
 *   6. Close — the parent re-fetches the uploads list; the parser does the rest.
 */
export default function UploadForm({
  onCancel,
  onUploaded,
}: {
  onCancel: () => void;
  onUploaded: () => void;
}) {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [accountsErr, setAccountsErr] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<"idle" | "hashing" | "initing" | "uploading" | "done" | "error">("idle");
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .list()
      .then((items) => {
        if (cancelled) return;
        setAccounts(items.filter((a) => a.active));
      })
      .catch((e: Error) => {
        if (!cancelled) setAccountsErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!accountId) {
      setError("Pick an account.");
      return;
    }
    if (!file) {
      setError("Pick a file.");
      return;
    }
    if (file.size <= 0) {
      setError("File is empty.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB; cap is 100 MB.`);
      return;
    }

    try {
      setPhase("hashing");
      const sha256 = await sha256OfFile(file);

      setPhase("initing");
      const { presigned } = await uploadsApi.init({
        account_id: accountId,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        file_size: file.size,
        sha256,
      });

      setPhase("uploading");
      setProgress(0);
      await putToPresignedUrl(presigned, file, (loaded, total) => {
        setProgress(total > 0 ? Math.round((loaded / total) * 100) : 0);
      });

      setPhase("done");
      onUploaded();
    } catch (e: unknown) {
      setPhase("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const busy = phase === "hashing" || phase === "initing" || phase === "uploading";

  return (
    <form className="form" onSubmit={submit}>
      <h2>Upload trade log</h2>

      <label>
        Account
        {accountsErr ? (
          <span className="error">Couldn't load accounts: {accountsErr}</span>
        ) : accounts === null ? (
          <select disabled>
            <option>Loading accounts…</option>
          </select>
        ) : accounts.length === 0 ? (
          <span className="muted">No active accounts. Add one first.</span>
        ) : (
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            required
            disabled={busy}
          >
            <option value="">— pick an account —</option>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.name} — {a.prop_firm}
              </option>
            ))}
          </select>
        )}
      </label>

      <label>
        File (CSV / XLSX / PDF, up to 100 MB)
        <input
          type="file"
          accept=".csv,.xlsx,.pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          required
          disabled={busy}
        />
        {file && (
          <small>
            {file.name} — {(file.size / 1024).toFixed(0)} KB
          </small>
        )}
      </label>

      {phase === "hashing" && <div className="muted">Hashing file…</div>}
      {phase === "initing" && <div className="muted">Requesting upload URL…</div>}
      {phase === "uploading" && (
        <div>
          <div className="muted">Uploading… {progress}%</div>
          <progress value={progress} max={100} style={{ width: "100%" }} />
        </div>
      )}
      {phase === "done" && <div className="muted">Uploaded. The parser is running in the background.</div>}

      {error && <div className="error">{error}</div>}

      <div className="form-actions">
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          {phase === "done" ? "Close" : "Cancel"}
        </button>
        {phase !== "done" && (
          <button type="submit" className="primary" disabled={busy}>
            {busy ? "Working…" : "Upload"}
          </button>
        )}
      </div>
    </form>
  );
}
